import React, { useState } from 'react';
import {
  Navbar,
  Nav,
  Container,
  Button,
  NavDropdown,
} from 'react-bootstrap';
import { Link, NavLink } from 'react-router-dom';
import { FaBars, FaTimes, FaInfoCircle, FaBook, FaBlog, FaNewspaper } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../Assets/logo.png';
import './Navbar.css';

const sidebarVariants = {
  hidden: {
    x: '-100%',
    transition: {
      type: 'tween',
      duration: 0.3,
    },
  },
  visible: {
    x: 0,
    transition: {
      type: 'tween',
      duration: 0.3,
    },
  },
};

const AppNavbar = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const { t, i18n } = useTranslation();

  // Toggle Sidebar
  const handleSidebarToggle = () => setShowSidebar(!showSidebar);

  // Change Language
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === 'ar' ? 'rtl' : 'ltr'; // Update text direction
  };

  return (
    <>
      {/* Main Navbar */}
      <Navbar
        bg="white"
        variant="light"
        expand="lg"
        sticky="top"
        className="navbar-custom"
        role="navigation"
      >
        <Container fluid>
          <div className="d-flex align-items-center w-100">
            {/* Toggle Button for Mobile */}
            <Button
              variant="outline-dark"
              onClick={handleSidebarToggle}
              className="toggle-button d-lg-none me-2"
              aria-label={t('navbar.menu')}
            >
              <FaBars />
            </Button>

            {/* Logo Section */}
            <Navbar.Brand as={Link} to="/" className="logo-section">
              <img src={logo} alt="Logo" className="logo" />
            </Navbar.Brand>

            {/* Navigation Links */}
            <Nav className="mx-auto d-none d-lg-flex align-items-center">
              <Nav.Link as={NavLink} to="/about" activeClassName="active">
                {t('navbar.about')}
              </Nav.Link>
              <Nav.Link as={NavLink} to="/Courseslist" activeClassName="active">
                {t('navbar.courses')}
              </Nav.Link>
              <Nav.Link as={NavLink} to="/blog" activeClassName="active">
                {t('navbar.blog')}
              </Nav.Link>
              <Nav.Link as={NavLink} to="/Library" activeClassName="active">
                {t('navbar.publication')}
              </Nav.Link>
            </Nav>

            {/* Language Switcher */}
            <div className="language-switcher d-none d-lg-flex align-items-center">
              <NavDropdown
                title={
                  <span className="dropdown-flag">
                    <ReactCountryFlag
                      countryCode={i18n.language === 'ar' ? 'EG' : 'US'}
                      svg
                      style={{ width: '1.2em', height: '1.2em' }}
                    />
                  </span>
                }
                id="language-dropdown"
                align="end"
              >
                <NavDropdown.Item onClick={() => changeLanguage('en')}>
                  <ReactCountryFlag
                    countryCode="US"
                    svg
                    style={{ width: '1.2em', height: '1.2em' }}
                  />{' '}
                  {t('language.english')}
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => changeLanguage('ar')}>
                  <ReactCountryFlag
                    countryCode="EG"
                    svg
                    style={{ width: '1.2em', height: '1.2em' }}
                  />{' '}
                  {t('language.arabic')}
                </NavDropdown.Item>
              </NavDropdown>
            </div>
          </div>
        </Container>
      </Navbar>

      {/* Sidebar with Framer Motion */}
      <AnimatePresence>
        {showSidebar && (
          <>
            {/* Overlay */}
            <motion.div
              className="ccf-drawer-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={handleSidebarToggle}
            />

            {/* Sidebar */}
            <motion.aside
              className="ccf-drawer"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={sidebarVariants}
            >
              {/* Sidebar Header */}
              <div className="ccf-drawer-header d-flex justify-content-between align-items-center">
                <img src={logo} alt="Centro Culturale Francescano" className="ccf-drawer-logo" />
                <Button
                  variant="link"
                  onClick={handleSidebarToggle}
                  aria-label={t('navbar.close')}
                  className="close-button"
                >
                  <FaTimes size={24} />
                </Button>
              </div>

              {/* Sidebar Navigation */}
              <nav className="ccf-drawer-nav">
                <Nav className="flex-column">
                  <Nav.Link as={NavLink} to="/about" onClick={handleSidebarToggle}>
                    <FaInfoCircle className="ccf-drawer-icon" />
                    {t('navbar.about')}
                  </Nav.Link>
                  <Nav.Link as={NavLink} to="/Courseslist" onClick={handleSidebarToggle}>
                    <FaBook className="ccf-drawer-icon" />
                    {t('navbar.courses')}
                  </Nav.Link>
                  <Nav.Link as={NavLink} to="/Library" onClick={handleSidebarToggle}>
                    <FaNewspaper className="ccf-drawer-icon" />
                    {t('navbar.publication')}
                  </Nav.Link>
                  <Nav.Link as={NavLink} to="/blog" onClick={handleSidebarToggle}>
                    <FaBlog className="ccf-drawer-icon" />
                    {t('navbar.blog')}
                  </Nav.Link>
                </Nav>
              </nav>

              {/* Language Switcher in Sidebar */}
              <div className="language-switcher">
                <button
                  className="lang-button mx-2"
                  onClick={() => changeLanguage('en')}
                  aria-label={t('language.english')}
                >
                  <ReactCountryFlag
                    countryCode="US"
                    svg
                    style={{ width: '1.5em', height: '1.5em' }}
                  />
                </button>
                <button
                  className="lang-button mx-2"
                  onClick={() => changeLanguage('ar')}
                  aria-label={t('language.arabic')}
                >
                  <ReactCountryFlag
                    countryCode="EG"
                    svg
                    style={{ width: '1.5em', height: '1.5em' }}
                  />
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AppNavbar;
